import { Badge } from "@patternfly/react-core";
import { memo, useCallback, useMemo } from "react";

import { SUPPORTED_MODALITIES, TAG_LATEST } from "../store/global-types/customization-types";
import { getVersionNumbers } from "../util/string-convert";

import './model-tag.css';

export enum ModelTagStyle {
    Default = 'default',
    Highlighted = 'highlighted',
}

interface ModelTagProps {
    isActive?: boolean;
    /** Optionally all relevant tags can be shown as a tooltip */
    allTags?: string[];
    onClick?: (tag: string) => void,
    allowTextSelect?: boolean,
    allowInteraction?: boolean,
    style?: ModelTagStyle,
    disableAutoColor?: boolean,
    /** Optional `data-testid` forwarded to the badge element. */
    testId?: string,
}

const getStyleClass = (style: ModelTagStyle) => {
    switch (style) {
        case ModelTagStyle.Highlighted:
            return 'model-tag-highlighted';
        case ModelTagStyle.Default:
        default:
            return '';
    }
}

/**
 * Get a class name with color styles for a tag based on its content: latest,
 * version number, or modality. Any other tags won't get any specific theming.
 */
const getAutocolor = (tag: string, disableAutoColor: boolean) => {
    if (disableAutoColor) {
        return '';
    }

    if (tag === TAG_LATEST) {
        // tag is 'latest'
        return 'model-tag-is-latest';
    } else if (getVersionNumbers([tag]).length > 0) {
        // tag looks like a version number
        return 'model-tag-is-version';
    } else if (SUPPORTED_MODALITIES.includes(tag)) {
        // tag looks like a modality
        return `model-tag-is-modality`;
    } else {
        // no special styling for other tags
        return '';
    }
}

/**
 * An interactable tag component that can be used to display model tags.
 * Provide the tag text as children.
 */
const ModelTag = (props: React.PropsWithChildren<ModelTagProps>) => {
    const { children, allTags, isActive, onClick, testId } = props;

    const allowTextSelect = props.allowTextSelect ?? false;
    const allowInteraction = props.allowInteraction ?? true;
    const style = props.style ?? ModelTagStyle.Default;
    const disableAutoColor = props.disableAutoColor ?? false;

    const handleClick = useCallback(() => {
        if (onClick && children) {
            onClick(children.toString());
        }
    }, [children, onClick]);

    const tagStyle = useMemo(() => {
        return [
            'model-tag',
            isActive ? 'is-active' : '',
            allowTextSelect ? 'allow-text-select' : '',
            allowInteraction ? 'allow-interaction' : '',
            getStyleClass(style),
            children ? getAutocolor(children.toString(), disableAutoColor) : '',
        ].filter(Boolean).join(' ');
    }, [allowInteraction, allowTextSelect, isActive, style, children, disableAutoColor]);

    return (
        <Badge
            className={tagStyle}
            title={allTags && allTags.length > 0 ? allTags.join(', ') : undefined}
            isRead={!isActive}
            onClick={onClick ? handleClick : undefined}
            data-testid={testId}
        >
            {children}
        </Badge>
    );
}

export default memo(ModelTag);
